import { useMemo, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { EmptyState } from "@/components/ui";
import { Calendar } from "lucide-react";
import { BookingCard, type BookingViewer } from "./BookingCard";
import { BookingsDaySectionHeader } from "./BookingsDaySectionHeader";
import type { CalendarDateKey, CalendarDayTotals } from "./bookingCalendar";
import { cn } from "@/lib/utils";
import type { BookingListRow } from "./bookingViewModel";

interface BookingsDayPanelProps {
  dateKey: CalendarDateKey;
  bookings: BookingListRow[];
  /** Whole-day totals for the header (the panel may show one page of a busy day). */
  totals: CalendarDayTotals;
  /** Paging controls under the cards when the day spans several pages. */
  pagination?: ReactNode;
  bookingsListPath: string;
  viewer: BookingViewer;
  processingByBookingId: Record<string, boolean>;
  fileInputResetKey: Record<string, number>;
  onReceiptUpload: (booking: BookingListRow, file?: File | null) => void;
  onEditSellerNotes?: (bookingId: string, currentNotes: string | null) => void;
}

function sortBookingsByVisit(bookings: BookingListRow[]): BookingListRow[] {
  return [...bookings].sort((a, b) => {
    const ta = a.visitAt ? new Date(a.visitAt).getTime() : Number.POSITIVE_INFINITY;
    const tb = b.visitAt ? new Date(b.visitAt).getTime() : Number.POSITIVE_INFINITY;
    if (ta !== tb) return ta - tb;
    return (a.timeSlot ?? "").localeCompare(b.timeSlot ?? "");
  });
}

export function BookingsDayPanel({
  dateKey,
  bookings,
  totals,
  pagination,
  bookingsListPath,
  viewer,
  processingByBookingId,
  fileInputResetKey,
  onReceiptUpload,
  onEditSellerNotes,
}: BookingsDayPanelProps) {
  const { t } = useTranslation("bookings");
  const orderedBookings = useMemo(
    () => sortBookingsByVisit(bookings),
    [bookings],
  );

  const renderBooking = (booking: BookingListRow) => (
    <BookingCard
      key={`${booking.type}-${booking.id}`}
      booking={booking}
      detailPath={`${bookingsListPath}/${booking.id}`}
      viewer={viewer}
      omitVisitDate
      isProcessingReceipt={processingByBookingId[booking.id] ?? false}
      fileInputResetKey={fileInputResetKey[booking.id] ?? 0}
      onReceiptUpload={(file) =>
        onReceiptUpload(booking, file)
      }
      onEditSellerNotes={onEditSellerNotes}
    />
  );

  return (
    <section
      id="bookings-day-panel"
      className="mt-4 scroll-mt-4 border-t border-surface-200/70 pt-4 sm:mt-5 sm:scroll-mt-6 sm:pt-5"
    >
      <BookingsDaySectionHeader
        dateKey={dateKey}
        bookingCount={totals.bookings}
        covers={totals.guests}
        idPrefix="bookings-day-panel"
        className="mb-3 sm:mb-4"
      />

      {bookings.length === 0 ? (
        <EmptyState
          icon={Calendar}
          title={t("calendar.emptyDay")}
          description={t("calendar.emptyDayDescription")}
        />
      ) : (
        <div
          className={cn(
            viewer === "partner"
              ? "-mx-4 flex flex-col gap-px bg-surface-200 sm:mx-0 sm:gap-3 sm:bg-transparent"
              : "space-y-3",
          )}
        >
          {orderedBookings.map(renderBooking)}
        </div>
      )}
      {pagination}
    </section>
  );
}
